import type { ReactNode } from 'react'
import { Info } from 'lucide-react'

import styles from './PageHeader.module.css'

export interface PageHeaderProps {
  /** The page's name; rendered as the page's <h1>. */
  title: ReactNode
  /** One line under the title, secondary text. */
  description?: ReactNode
  /** A small uppercase caption above the title (a section, an object type). */
  eyebrow?: ReactNode
  /** A row under the description: status pills, counts, ids. */
  meta?: ReactNode
  /** Right side, vertically centred on the title; put the primary action last. */
  actions?: ReactNode
  /** Under the header, on a hairline (underline tabs sit on it). */
  tabs?: ReactNode
  className?: string
}

/**
 * The top of every screen: title 24/32, optional details on demand, the page's
 * actions on the right and optional tabs underneath. The top bar already shows
 * where the operator is; this header says what the page is for.
 */
export function PageHeader({ title, description, eyebrow, meta, actions, tabs, className }: PageHeaderProps): JSX.Element {
  return (
    <header className={[styles.header, tabs ? styles.withTabs : '', className ?? ''].filter(Boolean).join(' ')}>
      <div className={styles.row}>
        <div className={styles.text}>
          {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{title}</h1>
            {description ? (
              <details className={styles.descriptionDisclosure}>
                <summary aria-label="Информация о разделе" title="Информация о разделе"><Info size={15} aria-hidden="true" /></summary>
                <div className={styles.descriptionPopover}>{description}</div>
              </details>
            ) : null}
          </div>
          {meta ? <div className={styles.meta}>{meta}</div> : null}
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {tabs ? <div className={styles.tabs}>{tabs}</div> : null}
    </header>
  )
}

export interface PageSectionProps {
  title: ReactNode
  description?: ReactNode
  /** Right side of the section heading: filters, a secondary action, a count. */
  actions?: ReactNode
  children: ReactNode
  id?: string
  className?: string
}

/**
 * A titled block inside a page (section title 15/20). Sections stack with the
 * page's section gap; the content under the heading is free (cards, tables).
 */
export function PageSection({ title, description, actions, children, id, className }: PageSectionProps): JSX.Element {
  const headingId = id ? `${id}-title` : undefined
  return (
    <section id={id} aria-labelledby={headingId} className={[styles.section, className ?? ''].filter(Boolean).join(' ')}>
      <div className={styles.sectionHead}>
        <div className={styles.text}>
          <div className={styles.titleRow}>
            <h2 id={headingId} className={styles.sectionTitle}>
              {title}
            </h2>
            {description ? (
              <details className={styles.descriptionDisclosure}>
                <summary aria-label="Подробности раздела" title="Подробности"><Info size={14} aria-hidden="true" /></summary>
                <div className={styles.descriptionPopover}>{description}</div>
              </details>
            ) : null}
          </div>
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {children}
    </section>
  )
}
